/**
 * Vision lab: ask a question of a gold standard video ("how many people walk
 * past", "identify the cars"), have Gemini turn it into a plan for RF-DETR,
 * run it on the GPU, and see the boxes and the answer.
 *
 * The lab is the prototype for a layer a location, zone or camera could run
 * all the time, so a plan can be saved as a named recipe. GLOBAL records in
 * the admin database.
 */

import type { BaseDocument } from "./data";

export const VISION_SOURCE = "vision.service";
/** Detect, track and count on the GPU (VisionDetectQueue). */
export const VISION_DETECT_REQUESTED_DETAIL_TYPE = "vision.detect.requested";
/** Only makes the GPU scale up; the worker drops it (VisionDetectQueue). */
export const VISION_GPU_WARMUP_DETAIL_TYPE = "vision.gpu.warmup";
/** The GPU worker picked the run up (VisionRunQueue). */
export const VISION_DETECT_STARTED_DETAIL_TYPE = "vision.detect.started";
/** The GPU worker finished, or gave up (VisionRunQueue). */
export const VISION_DETECTIONS_READY_DETAIL_TYPE = "vision.detections.ready";

/**
 * - `Unique`: distinct tracks of the counted classes.
 * - `Line`: tracks crossing the segment a→b, by direction.
 * - `Area`: tracks inside the polygon, and those that walked into it.
 */
export enum VisionCountMode {
	Area = "area",
	Line = "line",
	Unique = "unique",
}

/**
 * The GPU worker, as the lab's status chip shows it.
 * - `NotDeployed`: this stage has no GPU worker (a personal stage).
 * - `Off`: scaled to zero.
 * - `Starting`: asked for, not running yet (an instance is booting).
 * - `Ready`: running.
 */
export enum VisionGpuState {
	NotDeployed = "not_deployed",
	Off = "off",
	Ready = "ready",
	Starting = "starting",
}

/**
 * - `WaitingForGpu`: queued for the GPU worker, which may be starting.
 * - `Detecting`: the worker has it.
 * - `Describing`: Gemini is describing tracked objects or boxing Gemini targets.
 * - `Answering`: Gemini is writing the answer.
 * - `Completed` / `Failed`: done.
 */
export enum VisionRunStatus {
	Answering = "answering",
	Completed = "completed",
	Describing = "describing",
	Detecting = "detecting",
	Failed = "failed",
	WaitingForGpu = "waiting_for_gpu",
}

export interface VisionCount {
	/** Polygon for `Area`, at least 3 points. */
	area?: VisionPoint[];
	/** COCO class names counted; the plan's detect classes when empty. */
	classes: string[];
	/**
	 * Segment for `Line`. "in" crosses from the left of a→b to its right, as
	 * you face from a to b on screen.
	 */
	line?: { a: VisionPoint; b: VisionPoint };
	mode: VisionCountMode;
}

/** `vision/runs/<id>/detections.json`, written by the GPU worker. */
export interface VisionDetectionsFile {
	counts: null | VisionRunCounts;
	durationSeconds: number;
	fps: number;
	/** Per sampled frame: [track id, x1, y1, x2, y2, confidence], normalized. */
	frames: {
		boxes: [number, number, number, number, number, number][];
		t: number;
	}[];
	height: number;
	/** Frames sampled for Gemini's targets, when the plan has any. */
	keyframes?: { key: string; t: number }[];
	tracks: Omit<VisionRunObject, "description" | "seenAt">[];
	width: number;
}

/** `vision.detections.ready` */
export interface VisionDetectionsReadyEvent {
	detectionsKey?: string;
	error?: string;
	gpuSeconds?: number;
	runId: string;
	trackCount: number;
}

/** `vision.detect.requested` */
export interface VisionDetectRequestedEvent {
	classes: string[];
	count?: VisionCount;
	/** The plan's focus point: the worker cuts a clip of each track that comes near it. */
	focusPoint?: VisionPoint;
	/** Seconds between frames saved for Gemini's targets; none when the plan has no targets. */
	keyframeSeconds?: number;
	maxKeyframes?: number;
	runId: string;
	videoKey: string;
}

/** `vision.detect.started` */
export interface VisionDetectStartedEvent {
	runId: string;
}

/**
 * How a tracked object met the plan's focus point, from the GPU worker. Its
 * clip runs from a little before it first came near to just after it was
 * closest, cut to the object and the point, with the point ringed.
 */
export interface VisionFocusApproach {
	approached: boolean;
	/** Seconds into the video when it was closest to the point. */
	closestT?: number;
	endT?: number;
	frames?: { key: string; t: number }[];
	startT?: number;
}

/** `vision/runs/<id>/gemini-boxes.json`: Gemini's boxes on the sampled frames. */
export interface VisionGeminiBoxesFile {
	frames: {
		boxes: { box: [number, number, number, number]; label: string }[];
		t: number;
	}[];
}

export interface VisionGpuStatus {
	desired: number;
	pending: number;
	running: number;
	state: VisionGpuState;
}

/** What the instruction turns into, before it runs. Editable, and what a recipe saves. */
export interface VisionPlan {
	/** What the answer should address, in the instruction's terms. */
	answerFocus: string;
	count?: VisionCount;
	/** Gemini looks at each tracked object's crops and answers this about it. */
	describe?: { classes: string[]; question: string };
	/** RF-DETR's classes, from the 80 COCO names (`COCO_CLASS_NAMES`). */
	detect: { classes: string[] };
	/**
	 * The plan in plain words, for the person to check, including what Gemini
	 * does instead of RF-DETR and why.
	 */
	explanation: string;
	/**
	 * A spot the person clicked, such as a badge reader. For each tracked
	 * object that comes near it, `describe` watches a short clip of the object
	 * there instead of looking at its crops.
	 */
	focusPoint?: VisionPoint;
	/**
	 * Things outside COCO's 80 classes (a rifle, a hi-vis vest on its own).
	 * Gemini boxes them on frames sampled from the video.
	 */
	geminiTargets?: { description: string; label: string }[];
}

/** A point in the frame, normalized to 0–1: x across, y down. */
export type VisionPoint = [number, number];

export interface VisionRecipe extends BaseDocument {
	createdBy?: string;
	/** The instruction the plan came from. */
	instruction: string;
	name: string;
	plan: VisionPlan;
}

export interface VisionRun extends BaseDocument {
	answer?: string;
	counts?: VisionRunCounts;
	createdBy?: string;
	/** `vision/runs/<id>/detections.json`: every frame's boxes. */
	detectionsKey?: string;
	/** When the GPU worker picked it up. */
	detectStartedAt?: Date;
	error?: string;
	finishedAt?: Date;
	/** `vision/runs/<id>/gemini-boxes.json`: Gemini's boxes for the plan's targets. */
	geminiBoxesKey?: string;
	/** Estimated from token counts at list prices. */
	geminiCostUsd?: number;
	gpuSeconds?: number;
	instruction: string;
	objects: VisionRunObject[];
	plan: VisionPlan;
	recipe?: string;
	status: VisionRunStatus;
	/** The gold standard video (`EvalVideo`). */
	video: string;
}

/** A finished run's boxes, as GET /shadow/vision/runs/{id}/boxes sends them. */
export interface VisionRunBoxes {
	detections?: VisionDetectionsFile;
	/** Every this-many sampled frame was kept to fit the response; 1 when none was dropped. */
	frameStride: number;
	geminiBoxes?: VisionGeminiBoxesFile;
}

/** The count the worker made, as `track/vision_job.py` reports it. */
export interface VisionRunCounts {
	byClass: Record<string, number>;
	/** Area: tracks seen outside the area, then inside. */
	entered?: number;
	/** Line: crossings from the left of a→b to its right. */
	in?: number;
	mode: VisionCountMode;
	/** Line: crossings the other way. */
	out?: number;
	/** Area: tracks ever inside. */
	present?: number;
	/** Unique and line: tracks counted. */
	total?: number;
}

/** One tracked object, with what Gemini said about it. */
export interface VisionRunObject {
	class: string;
	/** Counted by the plan's count rule. */
	counted: boolean;
	cropKeys: string[];
	description?: string;
	firstT: number;
	/** Set when the plan has a focus point. */
	focus?: VisionFocusApproach;
	frameCount: number;
	/** The track id on the boxes in the detections file. */
	id: number;
	lastT: number;
	/** Seconds into the video when Gemini saw what it describes, from a focus clip. */
	seenAt?: number;
}

/** A run as the lab page reads it, with signed URLs for its crops. */
export interface VisionRunView extends VisionRun {
	/** Signed URL of each object's first crop, by object id. */
	cropUrls: Record<string, string>;
}
